import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormattedMessage, useIntl } from 'react-intl'
import { filter, groupBy, map, sortBy } from 'lodash'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import FormHelperText from '@mui/material/FormHelperText'
import FormikTextField from '../../../../shared/components/formik/FormikTextField'
import { useNodes } from '../../../../contexts/NodesContext'
import { useAddresses } from '../../../../contexts/AddressesContext'

const DomainForm = ({ domain, createDomain, updateDomain, onSuccess, onCancel }) => {
  const intl = useIntl()
  const isEditMode = Boolean(domain)
  const { nodesById, loading: nodesLoading } = useNodes()
  const { addresses, loading: addressesLoading } = useAddresses()

  const ipv4Addresses = filter(addresses, (addr) => addr.addressType === 'ipv4')
  const addressesByNode = groupBy(ipv4Addresses, 'nodeId')
  const sortedNodeIds = sortBy(Object.keys(addressesByNode), (nodeId) => {
    const node = nodesById[nodeId]
    return node?.name?.toLowerCase() || ''
  })

  const validationSchema = Yup.object({
    domain: Yup.string().required(intl.formatMessage({ id: 'validation.required' })),
    addressId: Yup.string().required(intl.formatMessage({ id: 'validation.required' }))
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      domain: domain?.domain || '',
      addressId: domain?.addressId || ''
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const selectedAddress = addresses.find((a) => a.id === values.addressId)
      const domainData = {
        domain: {
          domain: values.domain,
          addressId: values.addressId,
          nodeId: selectedAddress?.nodeId
        }
      }

      const apiCall = isEditMode
        ? updateDomain(domain.id, domainData)
        : createDomain(domainData)

      apiCall
        .then(() => {
          onSuccess()
        })
        .catch((err) => {
          setStatus(err.message || 'Error saving domain')
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  const addressSelectError = formik.touched.addressId && Boolean(formik.errors.addressId)

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <FormikTextField
        formik={formik}
        name="domain"
        label={<FormattedMessage id="domains.form.domainLabel" />}
        autoFocus
      />

      <Box sx={{ mt: 2 }}>
        <FormControl fullWidth error={addressSelectError} disabled={nodesLoading || addressesLoading}>
          <InputLabel id="address-select-label">
            <FormattedMessage id="domains.form.addressLabel" />
          </InputLabel>
          <Select
            labelId="address-select-label"
            id="addressId"
            name="addressId"
            value={formik.values.addressId}
            label={<FormattedMessage id="domains.form.addressLabel" />}
            onChange={(e) => formik.setFieldValue('addressId', e.target.value)}
            onBlur={formik.handleBlur}
          >
            {map(sortedNodeIds, (nodeId) => {
              const node = nodesById[nodeId]
              const nodeAddresses = sortBy(addressesByNode[nodeId], 'address')
              return [
                <ListSubheader key={`header-${nodeId}`}>
                  {node?.name || nodeId}
                </ListSubheader>,
                ...map(nodeAddresses, (address) => (
                  <MenuItem key={address.id} value={address.id}>
                    {address.address} ({address.addressType})
                  </MenuItem>
                ))
              ]
            })}
          </Select>
          {addressSelectError && (
            <FormHelperText>{formik.errors.addressId}</FormHelperText>
          )}
        </FormControl>
      </Box>

      {formik.status && (
        <Typography color="error" sx={{ mt: 2 }}>
          {formik.status}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={formik.isSubmitting || nodesLoading || addressesLoading}
        >
          <FormattedMessage id="domains.form.submitButton" />
        </Button>
      </Box>
    </Box>
  )
}

export default DomainForm
